import React, { useEffect, useState } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { FaMoneyBill } from "react-icons/fa";
import InputMoney from "../Common/InputMoney";
import { FormPagamentosData, FormaPagamento } from "./types";
import {
  calculateSaldoDevedor,
  calculateValorComDesconto,
  formatPrice,
  validateDuasFormas,
} from "./utils";

interface FormPagamentosProps {
  valorTotal: number;
  onChange: (data: FormPagamentosData) => void;
  initialData?: FormPagamentosData;
}

const FormPagamentos: React.FC<FormPagamentosProps> = ({
  valorTotal,
  onChange,
  initialData,
}) => {
  const [data, setData] = useState<FormPagamentosData>({
    valorTotal: valorTotal,
    descontoPercentual: 0,
    valorComDesconto: valorTotal,
    valorEntrada: 0,
    formaPagamentoEntrada: "",
    valorFormaPagamento1: 0,
    saldoDevedor: valorTotal,
    valorFormaPagamento2: 0,
    usaDuasFormas: false,
    formaPagamento2: "",
    descontoEscopo: "total",
    valorEntradaComDesconto: 0,
    descontoPercentualForma2: 0,
    valorFormaPagamento2ComDesconto: 0,
  });

  const [erroValidacao, setErroValidacao] = useState<string>("");

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      valorTotal: valorTotal,
    }));
  }, [valorTotal]);

  // Atualizar valor com desconto (se escopo for total)
  useEffect(() => {
    const novoValorComDesconto =
      data.descontoEscopo === "total"
        ? calculateValorComDesconto(data.valorTotal, data.descontoPercentual)
        : data.valorTotal;
    setData((prev) => ({
      ...prev,
      valorComDesconto: novoValorComDesconto,
    }));
  }, [data.valorTotal, data.descontoPercentual, data.descontoEscopo]);

  // Atualizar entrada com desconto (se escopo for entrada)
  useEffect(() => {
    const novaEntradaComDesconto =
      data.descontoEscopo === "entrada"
        ? calculateValorComDesconto(data.valorEntrada, data.descontoPercentual)
        : data.valorEntrada;
    setData((prev) => ({
      ...prev,
      valorEntradaComDesconto: novaEntradaComDesconto,
    }));
  }, [data.valorEntrada, data.descontoPercentual, data.descontoEscopo]);

  // Atualizar saldo devedor (abate 2ª forma quando preenchida)
  useEffect(() => {
    // Entrada usada no saldo sempre SEM desconto (ex.: 390 − 117 = 273)
    const entradaParaSaldo = data.valorEntrada;

    const novoSaldo = calculateSaldoDevedor(
      data.valorComDesconto, // se escopo "total", já vem com desconto; caso contrário, igual ao valor total
      entradaParaSaldo,
      data.valorFormaPagamento2 || 0
    );

    setData((prev) => ({
      ...prev,
      saldoDevedor: novoSaldo,
    }));
  }, [data.valorComDesconto, data.valorEntrada, data.valorFormaPagamento2]);

  // Atualizar valor da 2ª forma com desconto próprio (não interfere no saldo)
  useEffect(() => {
    const percentual = data.descontoPercentualForma2 || 0;
    const valor = data.valorFormaPagamento2 || 0;
    const valorComDesc = calculateValorComDesconto(valor, percentual);
    setData((prev) => ({
      ...prev,
      valorFormaPagamento2ComDesconto: valorComDesc,
    }));
  }, [data.valorFormaPagamento2, data.descontoPercentualForma2]);

  // (Removido) Efeito antigo que aplicava desconto da entrada no saldo

  // Validar quando usar duas formas de pagamento
  useEffect(() => {
    if (data.usaDuasFormas) {
      const valor1 = data.valorFormaPagamento1 || 0;
      const valor2 = data.valorFormaPagamento2 || 0;

      const entradaEsperada =
        data.descontoEscopo === "entrada"
          ? data.valorEntradaComDesconto
          : data.valorEntrada;

      // Validar se a soma das duas formas é igual à entrada (considerando desconto quando aplicável)
      if (
        entradaEsperada > 0 &&
        !validateDuasFormas(valor1, valor2, entradaEsperada)
      ) {
      } else {
        setErroValidacao("");
      }
    } else {
      setErroValidacao("");
    }
  }, [
    data.usaDuasFormas,
    data.valorFormaPagamento1,
    data.valorFormaPagamento2,
    data.valorEntrada,
    data.valorEntradaComDesconto,
    data.descontoEscopo,
  ]);

  // Notificar mudanças para o componente pai
  useEffect(() => {
    onChange(data);
  }, [data, onChange]);

  const handleChange = (field: keyof FormPagamentosData, value: any) => {
    // Validar valores numéricos para evitar NaN
    if (typeof value === "number" && isNaN(value)) {
      value = 0;
    }
    // Segurança: ao desmarcar duas formas, zerar campos relacionados da 2ª forma
    if (field === "usaDuasFormas") {
      setData((prev) => {
        const next = { ...prev, usaDuasFormas: value };
        if (!value) {
          next.formaPagamento2 = "";
          next.valorFormaPagamento2 = 0;
          next.descontoPercentualForma2 = 0;
          next.valorFormaPagamento2ComDesconto = 0;
        }
        return next;
      });
      return;
    }
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const formasPagamento: FormaPagamento[] = [
    { value: "dinheiro", label: "Dinheiro" },
    { value: "pix", label: "PIX" },
    { value: "cartao_debito", label: "Cartão Débito" },
    { value: "cartao_credito", label: "Cartão Crédito" },
    { value: "transferencia", label: "Transferência" },
  ];

  return (
    <div
      className="border rounded p-3 mb-3"
      style={{ backgroundColor: "#d6ecff" }}
    >
      <h6 className="mb-3">
        {" "}
        <FaMoneyBill size={20} /> Informações de Pagamento
      </h6>
      {/* Seção: Valores e Desconto */}
      <Row>
        <Col md={3}>
          <InputMoney
            label="Valor Total"
            value={data.valorTotal}
            onChange={(value) => handleChange("valorTotal", value)}
          />
        </Col>
        <Col md={3}>
          <InputMoney
            label="Valor da Entrada"
            value={data.valorEntrada}
            onChange={(value) => handleChange("valorEntrada", value)}
          />
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Desconto (%)</Form.Label>
            <Form.Control
              type="number"
              onChange={(e) => {
                let valor = e.target.value;
                if (valor.length > 5) {
                  valor = valor.slice(0, 5);
                }
                const numero = parseFloat(valor);
                handleChange("descontoPercentual", isNaN(numero) ? 0 : numero);
              }}
              placeholder="Desconto em %"
              min={0}
              max={100}
              step={0.01}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Forma de Desconto</Form.Label>
            <Form.Select
              value={data.descontoEscopo}
              onChange={(e) =>
                handleChange(
                  "descontoEscopo",
                  e.target.value as "total" | "entrada"
                )
              }
            >
              <option value="total">Aplicar no Total</option>
              <option value="entrada">Aplicar na Entrada</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      {/* Mostrar entrada com desconto quando escopo for 'entrada' */}
      {data.descontoEscopo === "entrada" &&
        data.descontoPercentual > 0 &&
        data.valorEntrada > 0 && (
          <>
            <Alert variant="warning" className="py-2 w-100">
              <span>
                {` O valor da entrada com desconto de ${
                  data.descontoPercentual
                }% é: ${formatPrice(data.valorEntradaComDesconto)}`}
              </span>
            </Alert>
          </>
        )}

      {/* Seção: Entrada */}
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Forma de Pagamento</Form.Label>
            <Form.Select
              value={data.formaPagamentoEntrada}
              onChange={(e) =>
                handleChange("formaPagamentoEntrada", e.target.value)
              }
            >
              <option value="">Selecione...</option>
              {formasPagamento.map((forma) => (
                <option key={forma.value} value={forma.value}>
                  {forma.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="usaDuasFormas"
              label="Combinar duas formas de pagamento"
              checked={data.usaDuasFormas}
              onChange={(e) => handleChange("usaDuasFormas", e.target.checked)}
            />
          </Form.Group>
        </Col>
      </Row>
      <hr />

      {data.usaDuasFormas ? (
        <>
          {erroValidacao && (
            <Alert variant="warning" className="py-2">
              {erroValidacao}
            </Alert>
          )}

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>2ª Forma de Pagamento</Form.Label>
                <Form.Select
                  value={data.formaPagamento2}
                  onChange={(e) =>
                    handleChange("formaPagamento2", e.target.value)
                  }
                >
                  <option value="">Selecione...</option>
                  {formasPagamento.map((forma) => (
                    <option key={forma.value} value={forma.value}>
                      {forma.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <InputMoney
                label="Valor 2ª Forma"
                value={data.valorFormaPagamento2 || 0}
                onChange={(value) =>
                  handleChange("valorFormaPagamento2", value)
                }
              />
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Desconto (%)</Form.Label>
                <Form.Control
                  type="number"
                  onChange={(e) => {
                    let valor = e.target.value;
                    if (valor.length > 5) {
                      valor = valor.slice(0, 5);
                    }
                    const numero = parseFloat(valor);
                    handleChange(
                      "descontoPercentualForma2",
                      isNaN(numero) ? 0 : numero
                    );
                  }}
                  placeholder="Desconto em % da 2ª forma"
                  min={0}
                  max={100}
                  step={0.01}
                />
              </Form.Group>
            </Col>
          </Row>
            <Col md={12}>
              {data.valorFormaPagamento2 > 0 && (data.descontoPercentualForma2 || 0) > 0 && (
                <Alert variant="warning    " className="py-2">
                  {` Valor 2ª forma com desconto: ${formatPrice(
                    data.valorFormaPagamento2ComDesconto || 0
                  )}`}
                </Alert>
              )}
            </Col>
        </>
      ) : (
        <></>
      )}
      <Row>
        <Col md={12}>
          <div className="p-3 bg-light rounded border">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Saldo Devedor:</span>
              <span className="fs-5 fw-bold text-primary">
                {formatPrice(data.saldoDevedor)}
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default FormPagamentos;
